import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, ShieldCheck, CheckCircle2, Truck, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { errMsg, money } from "../lib/api";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { todayIST } from "../lib/datetime";

// Same option lists the Add Product screen uses, so a product created here is
// shaped exactly like one created there (owner instruction 20 Oct 2026: create
// the product from inside the purchase-inward screen when it isn't in the list).
// Owner rule (20 Oct 2026): these four are the only product types.
const CATEGORIES = ["Retail", "Technical", "Disposable", "Electrical"];
const QTY_UNITS = ["pcs", "grm", "ml"];
const STOCK_UNITS = ["bottle", "packet", "box", "pcs"];

export default function PurchaseInModal({ onClose, onSuccess }) {
  const [vendorName, setVendorName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIST());
  const [remarks, setRemarks] = useState("");
  const [availableSkus, setAvailableSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorOpen, setVendorOpen] = useState(false);
  // "Create product" from inside the inward screen: which line it was opened
  // for, the form, and the save flag.
  const [createFor, setCreateFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "", category: "Retail", unit: "pcs", stock_unit: "", qty: "", unit_cost: "", mrp: "",
  });
  const setNewField = (k, v) => setNewProduct((p) => ({ ...p, [k]: v }));

  // Owner request (24 Oct 2026): GST is entered as ONE total rupee figure
  // (the amount printed on the vendor PI), not a per-line percentage. It
  // defaults to 0 so the stored total is unchanged unless the clerk types it;
  // entering it makes the invoice total = items + GST, matching the printed PI.
  const [gstAmount, setGstAmount] = useState("");
  const [items, setItems] = useState([
    { sku_id: "", quantity: "", purchase_price: "" },
  ]);

  useEffect(() => {
    fetchSkus();
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  const fetchSkus = async () => {
    try {
      const res = await api.get("/inventory/master");
      setAvailableSkus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSkus(false);
    }
  };

  const addItemRow = () => {
    const defaultId = availableSkus[0]?.id || "";
    setItems([
      ...items,
      { sku_id: defaultId, quantity: "", purchase_price: "" },
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItemRow = (index, field, value) => {
    const next = [...items];
    next[index][field] = value;
    if (field === "sku_id") {
      const selected = availableSkus.find((s) => s.id === value);
      if (selected) {
        next[index].purchase_price = selected.unit_cost || 0;
      }
    }
    setItems(next);
  };

  // ---- create the product without leaving the invoice ----------------------
  const openCreateProduct = (index) => {
    setNewProduct({
      name: "",
      category: "Retail",
      unit: "pcs",
      stock_unit: "",
      qty: "",
      // Carry the price already typed on the line into the new product.
      unit_cost: items[index]?.purchase_price || "",
      mrp: "",
    });
    setCreateFor(index);
  };

  const handleCreateProduct = async (e) => {
    if (e) e.preventDefault();
    if (!newProduct.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/inventory/product", {
        name: newProduct.name.trim(),
        category: newProduct.category,
        ledger: newProduct.category === "Technical" ? "technical" : "retail",
        vendor_name: vendorName.trim(),
        unit: newProduct.unit,
        stock_unit: newProduct.stock_unit,
        qty: parseFloat(newProduct.qty) || 0,
        unit_cost: parseFloat(newProduct.unit_cost) || 0,
        mrp: parseFloat(newProduct.mrp) || 0,
        store_qty: 0,
        min_stock: 0,
      });
      const created = res.data || {};
      // Refresh so every other line (and the next invoice) can pick it up.
      await fetchSkus();
      if (created.id && createFor !== null) {
        const next = [...items];
        next[createFor] = {
          ...next[createFor],
          sku_id: created.id,
          purchase_price: newProduct.unit_cost || next[createFor].purchase_price,
        };
        setItems(next);
      }
      toast.success(`'${created.name || newProduct.name.trim()}' created and selected on this line`);
      setCreateFor(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceNumber) {
      toast.error("Please enter an Invoice Number");
      return;
    }
    if (items.some((i) => !i.sku_id || i.quantity <= 0)) {
      toast.error("Please select products and valid quantities for all items");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/inventory/purchase-in", {
        method: "direct",
        vendor_name: vendorName,
        po_number: null,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        // GST is ONE total rupee figure for the whole invoice. An empty field
        // means "no GST" (0), keeping the stored total unchanged.
        gst_amount: Number(gstAmount) || 0,
        items: items.map((i) => ({
          sku_id: i.sku_id,
          quantity: i.quantity,
          purchase_price: i.purchase_price,
          mrp: i.mrp,
          batch_number: i.batch_number,
          expiry_date: i.expiry_date,
        })),
        remarks,
      });

      toast.success(`Successfully processed Purchase In invoice '${res.data.invoice_number}'!`);
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-slate-200 shadow-2xl rounded-2xl max-w-5xl min-h-[700px] w-full max-h-[90vh] flex flex-col text-slate-950 overflow-visible"
       onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-6 text-white flex items-center justify-between border-b border-amber-500/30 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-amber-400" />
              <span className="lss-overline text-amber-300">Goods Receiving Protocol</span>
            </div>
            <h3 className="font-serif-lux text-2xl font-bold tracking-tight text-white mt-1">
              Purchase In Entry & Goods Inward
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Vendor Name</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={vendorName}
                  onChange={(e) => { setVendorName(e.target.value); setVendorOpen(true); }}
                  onFocus={() => setVendorOpen(true)}
                  onBlur={() => setTimeout(() => setVendorOpen(false), 150)}
                  placeholder="Type or select vendor..."
                  className="lss-input w-full text-xs font-semibold"
                  autoComplete="off"
                />
                {vendorOpen && (() => {
                  const live = (vendors || [])
                    .filter((v) => (v.name || "").toLowerCase().includes(vendorName.trim().toLowerCase()))
                    .filter((v) => (availableSkus || []).some((s) => s.vendor_id && s.vendor_id === v.id));
                  return (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    <div className="py-1 max-h-56 overflow-y-auto">
                      {live.map((v) => (
                          <button
                            key={v.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); setVendorName(v.name); setVendorOpen(false); }}
                            className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-amber-50 hover:text-amber-900 transition"
                          >
                            {v.name}
                          </button>
                      ))}
                      {live.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400 font-semibold text-center">No matches — will save as typed</div>
                      )}
                    </div>
                  </div>
                  );
                })()}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Invoice Number</label>
              <input
                type="text"
                required
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="lss-input w-full text-xs font-semibold font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Invoice Date</label>
              <input
                type="date"
                required
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
              />
            </div>
          </div>

          {/* Line Items Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Received Line Items ({items.length})</span>
              <button
                type="button"
                onClick={addItemRow}
                className="lss-btn-outline px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add Product Line
              </button>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <table className="lss-table text-xs">
                <thead>
                  <tr>
                    <th className="min-w-[180px]">Product</th>
                    <th className="w-24">Qty</th>
                    <th className="w-28">Unit Price (₹)</th>
                    <th className="w-28">Amount (₹)</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, idx) => {
                    const selected = availableSkus.find((s) => s.id === row.sku_id);
                    const systemPrice = selected
                      ? Number(selected.unit_price ?? selected.unit_cost ?? 0)
                      : null;
                    return (
                    <tr key={idx}>
                      <td className="min-w-[240px]">
                        <SearchableSelect
                          options={availableSkus}
                          value={row.sku_id}
                          onChange={(val) => updateItemRow(idx, "sku_id", val)}
                          placeholder="Search product name..."
                          getOptionLabel={(s) => `${s.name} (${s.category})`}
                          getOptionValue={(s) => s.id}
                        />
                        <button
                          type="button"
                          onClick={() => openCreateProduct(idx)}
                          title="Create the product now and keep invoicing"
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-amber-700 transition"
                        >
                          <PackagePlus className="w-3 h-3" /> Not in the list? Create product
                        </button>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={row.quantity}
                          onChange={(e) => updateItemRow(idx, "quantity", e.target.value)}
                          className="lss-input w-full text-xs font-bold tabular"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="any"
                          value={row.purchase_price}
                          onChange={(e) => updateItemRow(idx, "purchase_price", e.target.value)}
                          className="lss-input w-full text-xs font-bold tabular"
                        />
                        {systemPrice !== null && (
                          <div className="mt-1 text-[10px] font-semibold text-slate-500 whitespace-nowrap">
                            System: {money(systemPrice)}
                          </div>
                        )}
                      </td>
                      <td className="text-right tabular font-bold text-[11px] text-slate-800">
                        {money((Number(row.quantity) || 0) * (Number(row.purchase_price) || 0))}
                      </td>
                      <td className="text-center">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItemRow(idx)}
                            className="p-1 text-slate-400 hover:text-rose-600 rounded transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoice totals + the GST input. GST is ONE rupee figure typed
              here (the amount printed on the vendor PI), not a per-line %.
              Subtotal (ex-GST) + GST = Invoice Total, which is what gets stored. */}
          {(() => {
            const subtotal = items.reduce(
              (t, r) => t + (Number(r.quantity) || 0) * (Number(r.purchase_price) || 0), 0);
            const gst = Number(gstAmount) || 0;
            return (
              <div className="flex justify-end">
                <div className="w-full sm:w-80 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                    <span>Subtotal (ex-GST)</span>
                    <span className="tabular">{money(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                    <label htmlFor="gst-amount" className="font-bold uppercase tracking-wide">Total GST (₹)</label>
                    <input
                      id="gst-amount"
                      type="number"
                      min="0"
                      step="any"
                      value={gstAmount}
                      onChange={(e) => setGstAmount(e.target.value)}
                      placeholder="0"
                      title="Total GST in rupees printed on the vendor bill"
                      className="lss-input w-28 text-xs font-bold tabular text-right"
                    />
                  </div>
                  <div className="flex justify-between text-sm font-extrabold text-slate-950 border-t border-slate-200 pt-1.5 mt-1">
                    <span>Invoice Total</span>
                    <span className="tabular">{money(subtotal + gst)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Remarks / Inward Notes</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Received in good condition via courier, verified against PO line items..."
              className="lss-input w-full text-xs font-semibold h-16"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-600 font-semibold">
            Auto creates <strong className="text-slate-950">Stock Ledger</strong> & batch records.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="lss-btn-gold px-6 py-2 text-xs font-bold uppercase tracking-wider shadow-md disabled:opacity-50"
            >
              {submitting ? "Processing Inward..." : "Save & Verify Inward"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>

    {/* Quick create product — opened from a line whose item isn't in the list.
        Stays inside the invoice: the invoice keeps everything already typed. */}
    {createFor !== null && (
      <div
        className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4"
        onClick={() => !creating && setCreateFor(null)}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white border border-slate-200 shadow-2xl rounded-2xl w-full max-w-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-5 py-4 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <PackagePlus className="w-5 h-5" />
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-100">New product</div>
                <div className="font-bold text-sm">Create while inwarding</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCreateFor(null)}
              className="p-1.5 rounded-lg hover:bg-white/15 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleCreateProduct} className="p-5 space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">
                Product Name <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                autoFocus
                required
                value={newProduct.name}
                onChange={(e) => setNewField("name", e.target.value)}
                placeholder="e.g. KERASTASE NUTRITIVE MASK 200ML"
                className="lss-input w-full text-xs font-semibold"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Category</label>
                <select
                  value={newProduct.category}
                  onChange={(e) => setNewField("category", e.target.value)}
                  className="lss-input w-full text-xs font-semibold cursor-pointer"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Measured In</label>
                <select
                  value={newProduct.unit}
                  onChange={(e) => setNewField("unit", e.target.value)}
                  className="lss-input w-full text-xs font-semibold cursor-pointer"
                >
                  {QTY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Stock Unit</label>
                <select
                  value={newProduct.stock_unit}
                  onChange={(e) => setNewField("stock_unit", e.target.value)}
                  className="lss-input w-full text-xs font-semibold cursor-pointer"
                >
                  <option value="">— none —</option>
                  {STOCK_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">
                  Pack Qty <span className="normal-case font-semibold text-slate-400">({newProduct.unit})</span>
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={newProduct.qty}
                  onChange={(e) => setNewField("qty", e.target.value)}
                  placeholder="e.g. 100"
                  className="lss-input w-full text-xs font-bold tabular"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Unit Cost (₹)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={newProduct.unit_cost}
                  onChange={(e) => setNewField("unit_cost", e.target.value)}
                  className="lss-input w-full text-xs font-bold tabular"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">MRP (₹)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={newProduct.mrp}
                  onChange={(e) => setNewField("mrp", e.target.value)}
                  className="lss-input w-full text-xs font-bold tabular"
                />
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] font-semibold text-slate-600">
              Vendor <strong className="text-slate-900">{vendorName.trim() || "as typed above"}</strong> is saved with this
              product. Opening stock stays <strong className="text-slate-900">0</strong> — the quantity you entered on this
              line is received through the invoice itself.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setCreateFor(null)}
                disabled={creating}
                className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="lss-btn-gold px-5 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              >
                {creating ? "Creating..." : "Create & attach to line"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    )}
    </>
  );
}
